import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Layout from '../components/common/Layout';
import { validateRequired, validateTextArea } from '../utils/formValidator';
import '../styles/AdminManagement.css';

const FeedbackManagementPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [response, setResponse] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiveError, setArchiveError] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, total: 0 });

  useEffect(() => {
    loadFeedbacks();
    loadStats();
  }, [searchQuery, statusFilter, pagination.currentPage]);

  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', pagination.currentPage);
      params.append('per_page', 10);

      const res = await api.get(`/feedbacks?${params.toString()}`);
      if (res.data.success) {
        setFeedbacks(res.data.data);
        setPagination({
          currentPage: res.data.meta.current_page,
          lastPage: res.data.meta.last_page,
          total: res.data.meta.total
        });
      }
    } catch (error) {
      console.error('Failed to load feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/feedbacks/statistics');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setResponse(item.admin_response || '');
    setFormError('');
    setValidationErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate response
    const responseValidation = validateRequired(response, 'Response');
    if (!responseValidation.valid) {
      newErrors.response = responseValidation.error;
    } else {
      const textAreaValidation = validateTextArea(response, { minLength: 5, maxLength: 3000 });
      if (!textAreaValidation.valid) {
        newErrors.response = textAreaValidation.error;
      }
    }

    // If there are validation errors, show them
    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      return;
    }

    setFormError('');
    try {
      await api.post(`/feedbacks/${selectedItem.id}/respond`, { admin_response: response });
      setShowModal(false);
      loadFeedbacks();
      loadStats();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = (id) => {
    setArchiveConfirmId(id);
    setArchiveError('');
  };

  const handleConfirmedDelete = async () => {
    try {
      await api.delete(`/feedbacks/${archiveConfirmId}`);
      setArchiveConfirmId(null);
      loadFeedbacks();
      loadStats();
    } catch (error) {
      setArchiveError('Failed to archive feedback. Please try again.');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = { new: 'badge-new', read: 'badge-read', responded: 'badge-responded' };
    return classes[status] || 'badge-new';
  };

  const renderStars = (rating) => {
    if (!rating) return '-';
    return rating + '/5';
  };

  return (
    <Layout>
      <div className="admin-management">
          <div className="page-header">
            <div className="header-content">
              <h1>Feedback</h1>
              <p>View and respond to user feedback</p>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.total || 0}</span><span className="stat-label">Total</span></div>
            </div>
            <div className="stat-card new">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.new || 0}</span><span className="stat-label">New</span></div>
            </div>
            <div className="stat-card responded">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.responded || 0}</span><span className="stat-label">Responded</span></div>
            </div>
            <div className="stat-card rating">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.average_rating || 0}</span><span className="stat-label">Avg Rating</span></div>
            </div>
          </div>

          <div className="filters-section">
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Search feedback..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="filter-dropdown">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="responded">Responded</option>
              </select>
            </div>
          </div>

          <div className="data-table-container">
            {loading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>From</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.length === 0 ? (
                    <tr><td colSpan="5" className="empty-state">No feedback found</td></tr>
                  ) : (
                    feedbacks.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">{item.name?.charAt(0).toUpperCase()}</div>
                            <div>
                              <div className="user-name">{item.name}</div>
                              <div className="user-email">{item.email}</div>
                              {item.phone && (
                                <div className="user-phone" style={{ fontSize: '0.85rem', color: '#666', marginTop: '2px' }}>
                                  {item.phone_country_code} {item.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{renderStars(item.rating)}</td>
                        <td><span className={`status-badge ${getStatusBadgeClass(item.status)}`}>{item.status}</span></td>
                        <td>{new Date(item.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-edit" onClick={() => handleOpenModal(item)} title="View & Respond">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            </button>
                            <button
                              className="btn-archive"
                              onClick={() => handleDelete(item.id)}
                              title="Archive"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
            {pagination.lastPage > 1 && (
              <div className="pagination">
                <button disabled={pagination.currentPage === 1} onClick={() => setPagination(p => ({...p, currentPage: p.currentPage - 1}))}>Previous</button>
                <span>Page {pagination.currentPage} of {pagination.lastPage}</span>
                <button disabled={pagination.currentPage === pagination.lastPage} onClick={() => setPagination(p => ({...p, currentPage: p.currentPage + 1}))}>Next</button>
              </div>
            )}
          </div>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <h2>Feedback Details</h2>
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="feedback-details">
                <div className="feedback-header">
                  <div>
                    <strong>{selectedItem?.name}</strong>
                    <span>{selectedItem?.email}</span>
                    {selectedItem?.phone && (
                      <span style={{ display: 'block', marginTop: '2px', fontSize: '0.9rem', color: '#666' }}>
                        {selectedItem?.phone_country_code} {selectedItem?.phone}
                      </span>
                    )}
                  </div>
                  <div className="feedback-rating">{renderStars(selectedItem?.rating)}</div>
                </div>
                <div className="feedback-message">{selectedItem?.message}</div>
                <div className="feedback-date">Submitted: {new Date(selectedItem?.created_at).toLocaleString()}</div>
              </div>

              {selectedItem?.admin_response && (
                <div className="previous-response">
                  <strong>Previous Response:</strong>
                  <p>{selectedItem.admin_response}</p>
                  <small>By {selectedItem.responder?.name} on {new Date(selectedItem.responded_at).toLocaleString()}</small>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{selectedItem?.admin_response ? 'Update Response' : 'Your Response'}</label>
                  <textarea 
                    rows="4" 
                    value={response} 
                    onChange={(e) => {
                      setResponse(e.target.value);
                      let error = null;
                      if (e.target.value.trim()) {
                        const r1 = validateRequired(e.target.value, 'Response');
                        if (!r1.valid) { error = r1.error; }
                        else {
                          const r2 = validateTextArea(e.target.value, { minLength: 5, maxLength: 3000 });
                          if (!r2.valid) error = r2.error;
                        }
                      }
                      setValidationErrors(prev => {
                        const updated = { ...prev };
                        if (error) { updated.response = error; } else { delete updated.response; }
                        return updated;
                      });
                    }} 
                    className={validationErrors.response ? 'error' : ''}
                    placeholder="Type your response to this feedback..." 
                  />
                  {validationErrors.response && (
                    <small className="error-message">{validationErrors.response}</small>
                  )}
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Close</button>
                  <button type="submit" className="btn-submit" disabled={Object.keys(validationErrors).length > 0}>Send Response</button>
                </div>
              </form>
            </div>
          </div>
        )}

      {archiveConfirmId !== null && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '16px', padding: '40px 36px', maxWidth: '420px', width: '90%', textAlign: 'center', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }} style={{ position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>&times;</button>
            <div style={{ fontSize: '44px', marginBottom: '12px' }}>🗄️</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#111827' }}>Archive This Feedback?</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '6px' }}>Are you sure you want to archive this feedback?</p>
            <p style={{ color: '#ef4444', fontSize: '13px', fontWeight: '600', marginBottom: '24px' }}>This cannot be undone.</p>
            {archiveError && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px' }}>{archiveError}</p>}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }} style={{ padding: '10px 28px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: '15px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
              <button onClick={handleConfirmedDelete} style={{ padding: '10px 28px', borderRadius: '8px', border: 'none', background: '#1a472a', color: '#fff', fontSize: '15px', cursor: 'pointer', fontWeight: '600' }}>Yes, Archive</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FeedbackManagementPage;
